import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { Icon, WorkingMark } from '@/components/brand/Brand'
import { useMotion } from '@/components/motion/motion'
import { cx } from '@/lib/cx'

/* The illustrative month-end close in the home hero. Software works through the machine rows
   (cobalt), then hands over to the accountant (yellow): "Ready for review". It loops while motion
   is on; paused or reduced, it shows the finished close. */

type RowKind = 'machine' | 'human'
type RowState = 'queued' | 'run' | 'done' | 'human'

type ConsoleRow = {
  kind: RowKind
  label: string
  /** Counted up from zero while the row runs. */
  count?: number
  value: (count: string) => ReactNode
}

const rows: ConsoleRow[] = [
  { kind: 'machine', label: 'Bank feeds matched', count: 1284, value: (count) => <><b>{count}</b> / 1,300</> },
  { kind: 'machine', label: 'Receipts attached', count: 97, value: (count) => <><b>{count}</b>%</> },
  { kind: 'machine', label: 'Payroll journal drafted', value: () => 'Drafted' },
  { kind: 'human', label: 'Exceptions for review', value: () => '3 items' },
  { kind: 'human', label: 'Accountant sign-off', value: () => 'Required' },
]

type ConsoleState = {
  rows: RowState[]
  counts: number[]
  running: boolean
  /** Rows finished, for the progress bar. */
  done: number
}

const finished: ConsoleState = {
  rows: rows.map((row) => (row.kind === 'machine' ? 'done' : 'human')),
  counts: rows.map((row) => row.count ?? 0),
  running: false,
  done: rows.length,
}

const starting: ConsoleState = {
  rows: rows.map(() => 'queued'),
  counts: rows.map(() => 0),
  running: true,
  done: 0,
}

const numberFormat = new Intl.NumberFormat('en-US')
const replaceAt = <T,>(items: T[], index: number, value: T) => items.map((item, i) => (i === index ? value : item))

export const CloseConsole = () => {
  const { paused } = useMotion()
  const [state, setState] = useState(finished)

  useEffect(() => {
    if (paused) return

    let cancelled = false
    const timers = new Set<number>()
    const frames = new Set<number>()

    const sleep = (ms: number) =>
      new Promise<void>((resolve) => {
        const timer = window.setTimeout(() => {
          timers.delete(timer)
          resolve()
        }, ms)
        timers.add(timer)
      })

    const countUp = (index: number, ms: number) =>
      new Promise<void>((resolve) => {
        const target = rows[index].count ?? 0
        const start = performance.now()
        const tick = (now: number) => {
          if (cancelled) return resolve()
          const progress = Math.max(0, Math.min((now - start) / ms, 1))
          const eased = 1 - Math.pow(1 - progress, 3)
          setState((current) => ({ ...current, counts: replaceAt(current.counts, index, Math.round(target * eased)) }))
          if (progress < 1) frames.add(requestAnimationFrame(tick))
          else resolve()
        }
        frames.add(requestAnimationFrame(tick))
      })

    const setRow = (index: number, rowState: RowState, done?: number) =>
      setState((current) => ({ ...current, rows: replaceAt(current.rows, index, rowState), done: done ?? current.done }))

    const loop = async () => {
      // Each run starts from the finished close.
      await sleep(0)
      if (cancelled) return
      setState(finished)
      while (!cancelled) {
        await sleep(2600)
        if (cancelled) return
        setState(starting)
        await sleep(700)
        for (const [index, row] of rows.entries()) {
          if (cancelled) return
          if (row.kind === 'machine') {
            setRow(index, 'run')
            await Promise.all([row.count ? countUp(index, 1300) : Promise.resolve(), sleep(1300)])
            if (cancelled) return
            setRow(index, 'done', index + 1)
          } else {
            await sleep(450)
            if (cancelled) return
            setRow(index, 'human', index + 1)
          }
        }
        setState((current) => ({ ...current, running: false }))
        await sleep(5200)
      }
    }

    void loop()

    return () => {
      cancelled = true
      timers.forEach((timer) => window.clearTimeout(timer))
      frames.forEach((frame) => cancelAnimationFrame(frame))
    }
  }, [paused])

  const view = paused ? finished : state

  return (
    <aside className={cx('console', !view.running && 'is-review')} aria-label="Illustrative month-end close workflow">
      <div className="console-head">
        <WorkingMark />
        <div className="console-title">
          <p>Month-end close</p>
          <p className="mono">August</p>
        </div>
        <span className={cx('chip', view.running ? 'chip-machine' : 'chip-signal')}>
          {view.running ? 'Running' : 'Ready for review'}
        </span>
      </div>
      <div className="console-progress" aria-hidden="true">
        <i style={{ '--p': view.done / rows.length } as CSSProperties} />
      </div>
      <ol className="console-rows">
        {rows.map((row, index) => (
          <li key={row.label} className="console-row" data-kind={row.kind} data-state={view.rows[index]}>
            <span className="console-st" aria-hidden="true">
              <Icon name={row.kind === 'machine' ? 'check' : 'person'} />
            </span>
            <span className="console-row-label">{row.label}</span>
            <span className="console-row-value mono">{row.value(numberFormat.format(view.counts[index]))}</span>
          </li>
        ))}
      </ol>
    </aside>
  )
}
